// The three bands on Available when the caller holds the queue grant.
//
// A queue pick is a bank account with a proposed owner attached. `requested_by` is
// what tells a colleague's request from a morning proposal — not `source`. A band
// whose label comes from a different field than its sort key drifts the first time
// one of them is set without the other.

import type { ApprovalPick, BookAccount } from '../api/types';
import { bankAgeDays } from './bank.ts';
import { RELATIONSHIP_RANK, bestSignalScore, relationshipPill, verticalReading } from './boardCells.ts';
import { pickSignal, sourceLabel } from './queueSignal.ts';
import { engagementSortValue, fitSortValue } from './scores.ts';
import type { SortState } from './sort.ts';
import { sortBy } from './sort.ts';

export type AvailableBand = 'requests' | 'recommended' | 'bank';

export interface AvailableRow {
  id: string;
  band: AvailableBand;
  account: BookAccount | null;
  pick: ApprovalPick | null;
}

export interface AvailableBands {
  requests: AvailableRow[];
  recommended: AvailableRow[];
  bank: AvailableRow[];
}

export const BAND_ORDER: AvailableBand[] = ['requests', 'recommended', 'bank'];

export const BAND_LABEL: Record<AvailableBand, string> = {
  requests: 'Requests',
  recommended: 'Recommended routes',
  bank: 'The rest of the bank',
};

export function rowName(row: AvailableRow): string {
  return row.account?.account || row.pick?.account || row.id;
}

// Sort order only. The score itself is retired from every strategist-facing cell (2 Oct 2026).
export function rowFit(row: AvailableRow): number | null {
  const fromAccount = row.account?.scout_fit_score;
  if (fromAccount != null) return fromAccount;
  return row.pick?.fit_score ?? null;
}

export function bandRows(
  bank: BookAccount[],
  picks: ApprovalPick[],
): AvailableBands {
  const byId = new Map(bank.map(account => [account.id, account]));
  const picked = new Set(picks.map(pick => pick.account_id));

  const requests: AvailableRow[] = [];
  const recommended: AvailableRow[] = [];
  for (const pick of picks) {
    const row: AvailableRow = {
      id: pick.requested_by ? `${pick.account_id}::${pick.requested_by}` : pick.account_id,
      band: pick.requested_by ? 'requests' : 'recommended',
      account: byId.get(pick.account_id) ?? null,
      pick,
    };
    if (row.band === 'requests') requests.push(row);
    else recommended.push(row);
  }

  const rest: AvailableRow[] = [];
  for (const account of bank) {
    if (picked.has(account.id)) continue;
    rest.push({ id: account.id, band: 'bank', account, pick: null });
  }

  return { requests, recommended, bank: rest };
}

// Worklist order is the payload order inside each band. A keyed sort never
// lifts a row out of its band — that is the point of folding the queue in.
export function sortBand(
  rows: AvailableRow[],
  sort: SortState,
  today: Date,
): AvailableRow[] {
  return sortBy(
    rows,
    sort,
    row => bandSortValue(row, sort.key, today),
    rowName,
  );
}

export function sortBands(
  bands: AvailableBands,
  sort: SortState,
  today: Date,
): AvailableBands {
  return {
    requests: sortBand(bands.requests, sort, today),
    recommended: sortBand(bands.recommended, sort, today),
    bank: sortBand(bands.bank, sort, today),
  };
}

export function bandSortValue(
  row: AvailableRow,
  key: string | null,
  today: Date,
): string | number | null {
  if (!key) return null;
  if (key === 'account') return rowName(row);
  if (key === 'fit') return rowFit(row);
  if (key === 'signal') {
    if (row.pick) return pickSignal(row.pick) || null;
    // A bank row sorts as the board's Why now does.
    return row.account ? bestSignalScore({ ...row.account, ranking: null, tag: null }) : null;
  }
  if (key === 'updated') {
    return row.account ? bankAgeDays(row.account, today) : null;
  }
  // The board's own sort values for the columns Available shares with it.
  if (key === 'vertical') return row.account ? (verticalReading(row.account)?.label ?? null) : null;
  if (key === 'relationship_pill') {
    return row.account ? RELATIONSHIP_RANK[relationshipPill(row.account).kind] : null;
  }
  if (key === 'credits_30') return row.account?.credits_30 ?? null;
  // Fit and Engagement, as the board sorts them; an unscored row (or a pick with no bank account) is null.
  if (key === 'fit_score') return row.account ? fitSortValue(row.account) : null;
  if (key === 'engagement_score') return row.account ? engagementSortValue(row.account) : null;
  if (key === 'strategist') return row.pick?.owner || null;
  if (key === 'source') {
    const source = row.account?.scout_source || row.pick?.source;
    return source ? sourceLabel(source) : null;
  }
  return null;
}

export function competingAskCounts(rows: AvailableRow[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const accountId = row.pick?.account_id;
    if (!accountId || !row.pick?.requested_by) continue;
    counts.set(accountId, (counts.get(accountId) || 0) + 1);
  }
  return counts;
}

export function visibleBands(bands: AvailableBands): AvailableBand[] {
  return BAND_ORDER.filter(band => bands[band].length > 0);
}
